import { store } from '../../store'
import { updateToastData } from '../../toast/toastSlice'
import { apiEndPoints, apiHeaders } from '../../utils/apiConstants'
import { getUserFromLocalStorage } from '../../utils/utils'
import {
	LOAD_ERROR_MESSAGE,
	TURNSTILE_SITE_KEY,
	CAPTCHA_DISABLED,
	VERIFIED_TTL_MS,
	WARMUP_TIMEOUT_MS,
} from './constants'
import { loadTurnstileScript } from './turnstileScript'

const EXEMPT_URL_PARTS = [
	'/mypeeguuser/v1/login',
	'/mypeeguuser/v1/verify-turnstile',
	'/mypeeguuser/v1/logout',
	'/mypeeguuser/v1/forgotpassword',
	'/mypeeguuser/v1/resetpassword',
	'/mypeeguuser/v1/activate',
	'/mypeeguuser/v1/validatetoken',
	'/mypeeguuser/v1/resendactivation',
]

function showLoadErrorToast() {
	store.dispatch(
		updateToastData({
			showToast: true,
			title: '',
			subTitle: LOAD_ERROR_MESSAGE,
			isSuccess: false,
			multipleError: [],
			direction: 'right',
		}),
	)
}

function verifyUrl() {
	const base = (process.env.REACT_APP_BASE_URL || '').replace(/\/$/, '')
	return `${base}${apiEndPoints.verifyTurnstile}`
}

/**
 * Central Turnstile state for authenticated dashboard APIs.
 * UI (provider) registers a controller; axios interceptor calls ensureHumanVerified().
 */
class CaptchaService {
	constructor() {
		this.verifiedUntil = 0
		this.controller = null
		this.challengeInFlight = null
		this.warmPromise = null
		this.loadErrorNotified = false
		this.listeners = new Set()
	}

	subscribe(listener) {
		this.listeners.add(listener)
		return () => this.listeners.delete(listener)
	}

	notify() {
		this.listeners.forEach((listener) => {
			try {
				listener()
			} catch {
				// ignore subscriber errors
			}
		})
	}

	registerController(controller) {
		if (this.controller === controller) return
		this.controller = controller
		this.notify()
	}

	unregisterController(controller) {
		if (this.controller === controller) {
			this.controller = null
		}
	}

	isVerified() {
		return Date.now() < this.verifiedUntil
	}

	markVerified(ttlMs = VERIFIED_TTL_MS) {
		this.verifiedUntil = Date.now() + ttlMs
		this.notify()
	}

	clearVerification() {
		this.verifiedUntil = 0
		this.challengeInFlight = null
		this.notify()
	}

	resetSession() {
		this.clearVerification()
		this.warmPromise = null
		this.loadErrorNotified = false
	}

	notifyLoadFailure() {
		if (this.loadErrorNotified) return
		this.loadErrorNotified = true
		showLoadErrorToast()
	}

	shouldChallenge(error) {
		if (CAPTCHA_DISABLED) return false
		const config = error?.config
		if (!config) return false
		if (config._skipSecurityChallenge || config._securityRetried) return false
		if (!getUserFromLocalStorage()?.authToken) return false
		if (!this.controller) return false

		const url = `${config.baseURL || ''}${config.url || ''}`
		if (EXEMPT_URL_PARTS.some((part) => url.includes(part))) return false

		const status = error?.response?.status
		if (status === 401) return true

		if (status === 403) {
			const data = error.response?.data || {}
			const mitigated = error.response?.headers?.['cf-mitigated']
			if (data.captchaRequired === true) return true
			if (mitigated === 'challenge') return true
			return false
		}

		return false
	}

	async verifyTokenOnServer(turnstileToken) {
		const token =
			typeof turnstileToken === 'string' ? turnstileToken.trim() : ''
		if (!token) return { ok: false, reason: 'missing' }

		const user = getUserFromLocalStorage()
		const headers = {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			'ngrok-skip-browser-warning': 'true',
			[apiHeaders.deviceType]: apiHeaders.web,
		}
		if (user?.authToken) {
			headers[apiHeaders.authToken] = user.authToken
		}

		const controller = new AbortController()
		const timer = setTimeout(() => controller.abort(), 12000)

		try {
			const response = await fetch(verifyUrl(), {
				method: 'POST',
				headers,
				body: JSON.stringify({ turnstileToken: token }),
				signal: controller.signal,
			})
			clearTimeout(timer)

			if (!response.ok) {
				return { ok: false, reason: 'rejected' }
			}

			const body = await response.json().catch(() => ({}))
			if (!body?.captchaTicket) {
				return { ok: false, reason: 'rejected' }
			}

			this.markVerified()
			return { ok: true }
		} catch (err) {
			clearTimeout(timer)
			if (err?.name === 'AbortError') {
				return { ok: false, reason: 'timeout' }
			}
			if (typeof navigator !== 'undefined' && navigator.onLine === false) {
				return { ok: false, reason: 'offline' }
			}
			return { ok: false, reason: 'network' }
		}
	}

	startWarmup() {
		if (CAPTCHA_DISABLED) {
			this.warmPromise = Promise.resolve()
			return this.warmPromise
		}
		if (this.isVerified()) {
			this.warmPromise = Promise.resolve()
			return this.warmPromise
		}
		if (this.warmPromise) return this.warmPromise

		if (!TURNSTILE_SITE_KEY) {
			this.notifyLoadFailure()
			this.warmPromise = Promise.resolve()
			return this.warmPromise
		}

		this.warmPromise = this.runWarmup().catch(() => {})
		return this.warmPromise
	}

	async runWarmup() {
		try {
			await loadTurnstileScript()
		} catch {
			this.notifyLoadFailure()
			return
		}

		if (!this.controller?.warmupHiddenWidget) return

		await new Promise((resolve) => {
			let settled = false
			const finish = () => {
				if (settled) return
				settled = true
				resolve()
			}

			const timeoutId = setTimeout(() => {
				this.controller?.cancelWarmup?.()
				finish()
			}, WARMUP_TIMEOUT_MS)

			this.controller
				.warmupHiddenWidget()
				.then(async (token) => {
					if (token) {
						await this.verifyTokenOnServer(token)
					}
				})
				.catch(() => {
					// Hidden warmup can fail without blocking the dashboard.
					// Visible challenge runs later only if an API is unauthorized.
				})
				.finally(() => {
					clearTimeout(timeoutId)
					finish()
				})
		})
	}

	ensureHumanVerified() {
		if (CAPTCHA_DISABLED) return Promise.resolve(true)
		if (this.isVerified()) return Promise.resolve(true)
		if (this.challengeInFlight) return this.challengeInFlight

		this.challengeInFlight = this.runChallenge()
			.then((ok) => ok)
			.finally(() => {
				this.challengeInFlight = null
			})

		return this.challengeInFlight
	}

	async runChallenge() {
		await this.startWarmup()
		if (this.isVerified()) return true

		if (!this.controller?.openChallenge) return false

		return new Promise((resolve) => {
			this.controller.openChallenge({
				onComplete: (ok) => resolve(Boolean(ok)),
			})
		})
	}
}

export const captchaService = new CaptchaService()
